import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { requestLabel } from "./AdminAnalytics";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new", label: "New", color: "bg-[#C5A880] text-[#1A1A1A]" },
  { value: "contacted", label: "Contacted", color: "bg-[#3A6FB0] text-[#F9F6F0]" },
  { value: "viewing", label: "Viewing", color: "bg-[#6B8E5A] text-[#F9F6F0]" },
  { value: "closed", label: "Closed", color: "bg-[#73706B] text-[#F9F6F0]" },
];

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);
  const [params, setParams] = useSearchParams();
  const langFilter = params.get("language");
  const devFilter = params.get("development");
  const sourceFilter = params.get("source");
  const pathFilter = params.get("path");
  const requestFilter = params.get("request");
  const clearParam = (k) => {
    const next = new URLSearchParams(params);
    next.delete(k);
    setParams(next);
  };

  const load = async () => {
    const { data } = await api.get("/admin/leads");
    setLeads(data);
  };
  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      const { data } = await api.put(`/admin/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}`);
    } catch (_err) {
      toast.error("Update failed");
    } finally {
      setUpdating(null);
    }
  };

  const scoped = leads
    ? leads.filter(
        (l) =>
          (!langFilter || (l.language || "unknown") === langFilter) &&
          (!devFilter || l.development_slug === devFilter) &&
          (!sourceFilter || (l.source_page || "Unknown") === sourceFilter) &&
          (!pathFilter || l.page_path === pathFilter) &&
          (!requestFilter || (l.request_type || "general").replace("_", "-") === requestFilter)
      )
    : null;
  const filtered = scoped ? (filter === "all" ? scoped : scoped.filter((l) => (l.status || "new") === filter)) : null;
  const counts = scoped
    ? STATUSES.reduce((acc, s) => {
        acc[s.value] = scoped.filter((l) => (l.status || "new") === s.value).length;
        return acc;
      }, {})
    : {};

  return (
    <div className="p-10 lg:p-16" data-testid="admin-leads">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-3">— Inbox</p>
      <h1 className="text-5xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Leads</h1>
      <p className="text-sm text-[#73706B] mb-10">Captured enquiries from the public site.</p>

      {(langFilter || devFilter || sourceFilter || pathFilter || requestFilter) && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-active-filters">
          {[
            ["language", langFilter, "Language"],
            ["development", devFilter, "Development"],
            ["source", sourceFilter, "Source"],
            ["path", pathFilter, "Page"],
            ["request", requestFilter && requestLabel(requestFilter), "Request"],
          ]
            .filter(([, v]) => v)
            .map(([k, v, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => clearParam(k)}
                data-testid={`leads-clear-${k}`}
                className="inline-flex items-center gap-2 bg-[#EFE9DC] px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-[#E5DFD5]"
              >
                {label}: {v === "unknown" ? "Unknown" : v} <X className="w-3 h-3" />
              </button>
            ))}
        </div>
      )}

      {/* Status filter */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="leads-status-filter">
        <button
          type="button"
          onClick={() => setFilter("all")}
          data-testid="leads-filter-all"
          className={`px-5 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
            filter === "all" ? "bg-[#1A1A1A] text-[#F9F6F0]" : "border border-[#E5DFD5] hover:border-[#1A1A1A]"
          }`}
        >
          All {scoped ? `(${scoped.length})` : ""}
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setFilter(s.value)}
            data-testid={`leads-filter-${s.value}`}
            className={`px-5 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
              filter === s.value ? "bg-[#1A1A1A] text-[#F9F6F0]" : "border border-[#E5DFD5] hover:border-[#1A1A1A]"
            }`}
          >
            {s.label} {scoped ? `(${counts[s.value] || 0})` : ""}
          </button>
        ))}
      </div>

      {filtered === null ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      ) : filtered.length === 0 ? (
        <p className="text-[#73706B]">No leads in this view.</p>
      ) : (
        <div className="border border-[#E5DFD5] bg-[#FFFFFF] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] border-b border-[#E5DFD5]">
                <th className="text-left px-5 py-3 font-normal">Date</th>
                <th className="text-left px-5 py-3 font-normal">Name</th>
                <th className="text-left px-5 py-3 font-normal">Email</th>
                <th className="text-left px-5 py-3 font-normal">Request</th>
                <th className="text-left px-5 py-3 font-normal">Interest</th>
                <th className="text-left px-5 py-3 font-normal">Lang</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const current = l.status || "new";
                return (
                  <tr key={l.id} data-testid={`admin-lead-${l.id}`} className="border-b border-[#E5DFD5] last:border-b-0 hover:bg-[#F9F6F0]">
                    <td className="px-5 py-4 text-xs text-[#73706B]">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-5 py-4">{l.name}</td>
                    <td className="px-5 py-4 text-[#1A1A1A]">{l.email}</td>
                    <td className="px-5 py-4">
                      <span className="inline-block px-2 py-0.5 bg-[#EFE9DC] text-[10px] tracking-[0.2em] uppercase">{l.request_type}</span>
                    </td>
                    <td className="px-5 py-4 text-xs text-[#73706B]">{l.development_slug || l.interest || "—"}</td>
                    <td className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-[#73706B]" data-testid={`lead-lang-${l.id}`}>{l.language || "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1" data-testid={`lead-status-${l.id}`}>
                        {STATUSES.map((s) => {
                          const active = current === s.value;
                          return (
                            <button
                              key={s.value}
                              type="button"
                              disabled={updating === l.id || active}
                              onClick={() => setStatus(l.id, s.value)}
                              data-testid={`lead-${l.id}-set-${s.value}`}
                              className={`px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase transition-all ${
                                active
                                  ? s.color
                                  : "border border-[#E5DFD5] text-[#73706B] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                              } ${updating === l.id && !active ? "opacity-50" : ""}`}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
